import { motion, useInView } from "framer-motion";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { SPRING_BOUNCE } from "./animations";
import superduckImage from "../../../assets/images/superduckstatic.png";

// Clip icon for holding polaroids
const ClipIcon = () => (
  <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C10.5 0 9 1 9 3V8H15V3C15 1 13.5 0 12 0Z" fill="#8B7355" />
    <rect x="8" y="6" width="8" height="6" rx="1" fill="#A0522D" />
    <circle cx="12" cy="9" r="2" fill="#8B7355" />
  </svg>
);

// Horizontal clothesline SVG for desktop
const ClotheslineSVG = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 800 60"
    preserveAspectRatio="none"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M 0,30 Q 200,45 400,30 Q 600,15 800,30"
      stroke="#8B7355"
      strokeWidth="3"
      fill="none"
      strokeLinecap="round"
    />
    <path
      d="M 0,30 Q 200,45 400,30 Q 600,15 800,30"
      stroke="#A0522D"
      strokeWidth="1"
      fill="none"
      strokeLinecap="round"
      strokeDasharray="4 8"
    />
  </svg>
);

// Vertical clothesline SVG for mobile
const VerticalClotheslineSVG = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 60 600"
    preserveAspectRatio="none"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M 30,0 Q 45,150 30,300 Q 15,450 30,600"
      stroke="#8B7355"
      strokeWidth="3"
      fill="none"
      strokeLinecap="round"
    />
    <path
      d="M 30,0 Q 45,150 30,300 Q 15,450 30,600"
      stroke="#A0522D"
      strokeWidth="1"
      fill="none"
      strokeLinecap="round"
      strokeDasharray="4 8"
    />
  </svg>
);

// Desktop flying duck - animated horizontal flight from left to right
const FlyingDuck = ({ isInView }) => {
  return (
    <motion.img
      src={superduckImage}
      alt="Flying duck"
      className="tw-pointer-events-none tw-absolute tw-z-20 tw-hidden tw-h-16 tw-w-16 md:tw-block md:tw-h-20 md:tw-w-20"
      style={{
        top: "-10px",
        left: "0%",
      }}
      initial={{ x: 0 }}
      animate={
        isInView
          ? {
              x: [0, 800, 0],
              y: [0, -20, 0, -20, 0],
              rotate: [0, 5, 0, -5, 0],
              scaleX: [1, 1, -1],
            }
          : { x: 0 }
      }
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  );
};

// Mobile flying duck - animated up and down loop, upright
// Flies from top all the way to the last polaroid
const FlyingDuckMobile = ({ isInView }) => {
  return (
    <motion.img
      src={superduckImage}
      alt="Flying duck"
      className="tw-pointer-events-none tw-absolute tw-z-20 tw-h-14 tw-w-14 md:tw-hidden"
      style={{
        top: "0px",
        right: "8px",
      }}
      initial={{ y: 0 }}
      animate={
        isInView
          ? {
              y: [0, 1100, 0],
              x: [0, 8, 0, -8, 0],
              rotate: [0, 5, 0, -5, 0],
            }
          : { y: 0 }
      }
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  );
};

// Polaroid card component
const PolaroidCard = ({ image, number, title, description, rotation, delay, isInView }) => (
  <motion.div
    className="tw-relative tw-w-full tw-max-w-[280px] tw-rounded-sm tw-bg-white tw-p-3 tw-pb-6 tw-shadow-lg md:tw-max-w-[280px]"
    style={{ transform: `rotate(${rotation}deg)` }}
    initial={{ opacity: 0, y: 50, rotate: rotation }}
    animate={isInView ? { opacity: 1, y: 0, rotate: rotation } : { opacity: 0, y: 50 }}
    transition={{ ...SPRING_BOUNCE, delay }}
    whileHover={{
      scale: 1.05,
      rotate: 0,
      zIndex: 10,
      transition: { duration: 0.2 },
    }}
  >
    {/* Clip at top */}
    <div className="tw-absolute tw--top-6 tw-left-1/2 tw-z-10 tw--translate-x-1/2">
      <ClipIcon />
    </div>

    {/* Shadow/depth effect on polaroid */}
    <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-rounded-sm tw-bg-gradient-to-b tw-from-transparent tw-to-black/5" />

    {/* Image area */}
    <div className="tw-mb-4 tw-aspect-square tw-overflow-hidden tw-rounded-sm tw-bg-gray-100">
      <img src={image} alt={title} className="tw-h-full tw-w-full tw-object-cover" loading="lazy" />
    </div>

    {/* Caption area (thick white bottom like real polaroid) */}
    <div className="tw-px-2 tw-text-center">
      <span className="tw-text-xs tw-font-medium tw-tracking-wider tw-text-gray-400">{number}</span>
      <h3 className="tw-mb-1 tw-text-base tw-font-bold tw-text-gray-900 md:tw-text-lg">{title}</h3>
      <p className="tw-text-xs tw-leading-relaxed tw-text-gray-600 md:tw-text-sm">{description}</p>
    </div>
  </motion.div>
);

const TheJourney = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const stages = [
    {
      key: "apprentice",
      number: "01",
      rotation: -5,
      image: "/images/leader-journey-mentor.jpg",
    },
    {
      key: "leader",
      number: "02",
      rotation: 2,
      image: "/images/leader-journey-leader.png",
    },
    {
      key: "mentor",
      number: "03",
      rotation: -3,
      image: "/images/leader-journey-apprentice.png",
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="tw-overflow-hidden tw-bg-[#FFF8F0] tw-px-4 tw-py-[30px] md:tw-px-16 md:tw-py-16 lg:tw-px-24"
    >
      <div className="tw-mx-auto tw-max-w-6xl">
        {/* Title */}
        <motion.div
          className="tw-mb-12 tw-text-center md:tw-mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={SPRING_BOUNCE}
        >
          <h2 className="tw-mb-2 tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
            {t("leader_programme.journey.title")}
          </h2>
          <p className="tw-mx-auto tw-max-w-2xl tw-text-base tw-font-semibold tw-text-primary-main md:tw-text-lg">
            {t("leader_programme.journey.subtitle")}
          </p>
        </motion.div>

        {/* Desktop: Horizontal layout with clothesline and flying duck */}
        <div className="tw-relative tw-hidden md:tw-block">
          {/* Clothesline */}
          <ClotheslineSVG className="tw-absolute tw-left-0 tw-top-0 tw-z-0 tw-h-[60px] tw-w-full" />

          {/* Flying duck - horizontal animation */}
          <FlyingDuck isInView={isInView} />

          {/* Polaroids side by side */}
          <div className="tw-flex tw-items-start tw-justify-center tw-gap-6 tw-pt-8 lg:tw-gap-10">
            {stages.map((stage, index) => (
              <PolaroidCard
                key={stage.key}
                image={stage.image}
                number={stage.number}
                title={t(`leader_programme.journey.stages.${stage.key}.title`)}
                description={t(`leader_programme.journey.stages.${stage.key}.description`)}
                rotation={stage.rotation}
                delay={index * 0.15}
                isInView={isInView}
              />
            ))}
          </div>
        </div>

        {/* Mobile: Vertical polaroid layout with animated duck */}
        <div className="tw-relative md:tw-hidden">
          {/* Vertical clothesline on the right */}
          <VerticalClotheslineSVG className="tw-absolute tw-right-0 tw-top-0 tw-z-0 tw-h-full tw-w-[50px]" />

          {/* Flying duck - animated up and down */}
          <FlyingDuckMobile isInView={isInView} />

          {/* Polaroids stacked vertically */}
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-8 tw-pr-14 tw-pt-4">
            {stages.map((stage, index) => (
              <PolaroidCard
                key={stage.key}
                image={stage.image}
                number={stage.number}
                title={t(`leader_programme.journey.stages.${stage.key}.title`)}
                description={t(`leader_programme.journey.stages.${stage.key}.description`)}
                rotation={stage.rotation}
                delay={index * 0.15}
                isInView={isInView}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TheJourney;
